import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router';

import { errorReporter } from '@/shared/lib/errorReporting';
import { Button } from '@/shared/ui/Button';
import { Alert } from '@/shared/ui/primitives';

/** What is shown in place of a part of the interface that failed: what happened, and a way on. */
export function CrashMessage() {
  const { t } = useTranslation();
  return (
    <div role="alert" className="mx-auto max-w-xl py-10">
      <Alert tone="bad" title={t('crash.title')}>
        <p>{t('crash.text')}</p>
        <Button className="mt-3" onClick={() => window.location.reload()}>
          {t('crash.reload')}
        </Button>
      </Alert>
    </div>
  );
}

/**
 * For the router: a page that threw while loading or rendering. The frame of the
 * application stays, so the way to the other pages does too.
 */
export function RouteCrash() {
  const error = useRouteError();
  useEffect(() => {
    errorReporter.report(error, 'route');
  }, [error]);
  return <CrashMessage />;
}

interface BoundaryProps {
  children: ReactNode;
  /** Shown instead of the children once they have failed. */
  fallback?: ReactNode;
}

/**
 * Catches what the router's own handling does not reach (the providers around it).
 * Without a boundary React removes the whole page and leaves it blank.
 */
export class ErrorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    errorReporter.report(error, 'boundary');
    // For whoever looks at the console; the stack of components is not in the report.
    console.error(error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback ?? <PlainCrash />;
  }
}

/**
 * The last resort: said without translations or the kit, either of which may be
 * what failed.
 */
function PlainCrash() {
  return (
    <div role="alert" style={{ maxWidth: 480, margin: '15vh auto', padding: 24, font: '16px/1.5 system-ui, sans-serif' }}>
      <p style={{ fontWeight: 700 }}>Something went wrong.</p>
      <p>Reload the page. If it happens again, try later.</p>
      <button type="button" onClick={() => window.location.reload()} style={{ marginTop: 12, padding: '8px 16px' }}>
        Reload
      </button>
    </div>
  );
}
